import { type QueryFunctionContext, useQuery } from "@tanstack/react-query";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import {
  type PerformanceFilter,
  type PerformanceWithAccountFilter,
  fallbackToZero,
  largestToSmallest,
  lineChartFactory,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import eventsDataService from "features/Library/Events/services/eventsDataService";
import { CHART_PERIOD, type ChartPeriod } from "hooks/useChartPeriodMeasure";

export type QueryFilterEventId = QueryFunctionContext<
  [
    _: string,
    filter: PerformanceFilter | null,
    eventId?: number,
    sessionId?: number,
    sessionStart?: string,
    isOverview?: boolean,
    flowId?: number,
    isSession?: boolean,
  ],
  unknown
>;

export type QueryFilterEventIdSession = QueryFunctionContext<
  [
    _: string,
    filter: PerformanceFilter | null,
    eventId: number,
    sessionId: number,
    sessionStart: string,
    flowId?: number,
  ],
  unknown
>;

export type QueryFilterEventIdPeriod = QueryFunctionContext<
  [
    _: string,
    filter: PerformanceFilter | null,
    eventId: number | undefined,
    period: ChartPeriod,
    sessionId?: number,
    sessionStart?: string,
    flowId?: number,
  ],
  unknown
>;

export const getLineChartQuery = async ({ queryKey, signal }: QueryFilterEventIdPeriod) => {
  const { data } = await eventsDataService.getEventDensity(
    {
      ...queryKey[1],
      eventId: queryKey[2] ?? undefined,
      aggregation: queryKey[3],
      sessionId: queryKey[4],
      sessionStart: queryKey[5],
      flowId: queryKey[6],
    },
    signal!,
  );

  const lineData = lineChartFactory(data);

  return {
    lineData,
  };
};

type LineChartProps = {
  filter: PerformanceWithAccountFilter;
  eventId?: number;
  chartPeriod: ChartPeriod;
  reportEnabled: boolean;
  sessionId?: number;
  sessionDatetime?: string;
  flowId?: number;
};

export const useEventLineChartQuery = ({
  filter,
  eventId,
  chartPeriod,
  reportEnabled,
  sessionId,
  sessionDatetime,
  flowId,
}: LineChartProps) =>
  useQuery({
    queryKey: ["eventLineChart", filter, eventId, chartPeriod, sessionId, sessionDatetime, flowId],
    queryFn: getLineChartQuery,
    enabled: reportEnabled && chartPeriod !== CHART_PERIOD.UNSET && filter.type !== undefined,
  });

export type EventCardsDrillDown = {
  TotalRegistered: number;
  TotalAttended: number;
  AttendanceRate: string;
};

export type EventCards = EventCardsDrillDown & {
  TotalSessions: number;
};

const attendanceBody = (isSession?: boolean) =>
  isSession
    ? "The percentage of sessions attended compared to the total number of registrations."
    : "The percentage of events attended compared to the total number of registrations.";

export const getActivityByTypeQuery = async ({ queryKey, signal }: QueryFilterEventId) => {
  const { data } = await eventsDataService.getEventActivity(
    {
      ...queryKey[1],
      eventId: queryKey[2] ?? undefined,
      sessionId: queryKey[3],
      sessionStart: queryKey[4],
      flowId: queryKey[6],
      isSession: queryKey[7],
    },
    signal!,
  );

  const engagement: DataPoint[] = [
    {
      id: "registered",
      category: "Registered",
      value: fallbackToZero(data.Registered),
      fillColor: reportingColors.blue,
    },
    {
      id: "attended",
      category: "Attended",
      value: fallbackToZero(data.Attended),
      fillColor: reportingColors.greenDark,
    },
    {
      id: "notAttended",
      category: "Not Attended",
      value: fallbackToZero(data.NotAttended),
      fillColor: reportingColors.greyLight,
    },
  ];

  let cards: CardReportingItem[] = [];
  if (queryKey[5]) {
    // Is overview
    cards = [
      {
        statDescription: "Events",
        stat: fallbackToZero(data.Events),
        popupBody: "The total number of events in this account.",
      },
      {
        statDescription: "Registered",
        stat: fallbackToZero(data.Registered),
        popupBody: "The total number of users who registered for this event.",
      },
      {
        statDescription: "Attended",
        stat: fallbackToZero(data.Attended),
        popupBody: "The total number of users who attended this event.",
      },
      {
        statDescription: "Attendance Rate",
        stat: fallbackToZero(data.AttendanceRate * 100),
        isStatPercentage: true,
        popupBody: "The total percentage of events attended compared to the total number of registrations.",
      },
    ];
  } else {
    cards = [
      {
        statDescription: "Registered",
        stat: fallbackToZero(data.Registered),
        popupBody: "The total number of users who registered for this event.",
      },
      {
        statDescription: "Attended",
        stat: fallbackToZero(data.Attended),
        popupBody: "The total number of users who attended this event.",
      },
      {
        statDescription: "Not Attended",
        stat: fallbackToZero(data.NotAttended),
        popupBody: "The total number of users who registered for this event, but did not attend.",
      },
      {
        statDescription: "Attendance Rate",
        stat: fallbackToZero(data.AttendanceRate * 100),
        isStatPercentage: true,
        popupBody: attendanceBody(queryKey[7]),
      },
      {
        statDescription: "Sessions",
        stat: fallbackToZero(data.Sessions),
        popupBody: "The total number of sessions available for this event.",
      },
    ];
  }

  engagement.sort(largestToSmallest);

  return { engagement, cards };
};

type EngagementProps = {
  filter: PerformanceWithAccountFilter;
  eventId?: number;
  reportEnabled: boolean;
  sessionId?: number;
  sessionDatetime?: string;
  isOverview?: boolean;
  flowId?: number;
  isSession?: boolean;
};

export const useEventEngagementQuery = ({
  filter,
  eventId,
  reportEnabled,
  sessionId,
  sessionDatetime,
  isOverview,
  flowId,
  isSession,
}: EngagementProps) =>
  useQuery({
    queryKey: ["eventActivityByType", filter, eventId, sessionId, sessionDatetime, isOverview, flowId, isSession],
    queryFn: getActivityByTypeQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

export const getTableQuery = async ({ queryKey }: QueryFilterEventId) => {
  const { data } = await eventsDataService.getSessionDetail({
    ...queryKey[1],
    eventId: queryKey[2],
    flowId: queryKey[3],
  });

  const formattedData = data.map((item) => {
    const secondsInMinutes = 60;
    return {
      ...item,
      id: `${item.SessionId}-${item.SessionDatetime}`,
      SessionDuration: item.SessionDuration * secondsInMinutes, // The endpoint returns the duration in minutes, but how we use this data expects it in seconds.
    };
  });

  return formattedData;
};

export const useEventTableQuery = ({ filter, eventId, reportEnabled, flowId }: EngagementProps) =>
  useQuery({
    queryKey: ["eventTable", filter, eventId, flowId],
    queryFn: getTableQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

type DrillDownProps = EngagementProps & {
  eventId: number;
  sessionId: number;
  sessionDatetime: string;
  flowId?: number;
};

export const getDrillDownQuery = async ({ queryKey }: QueryFilterEventIdSession) => {
  const { data } = await eventsDataService.getEventSessionUserDetail({
    ...queryKey[1],
    eventId: queryKey[2],
    sessionId: queryKey[3],
    sessionStart: queryKey[4],
    flowId: queryKey[5],
  });

  const dataWithId = data.map((item) => {
    return {
      ...item,
      id: item.UserId,
    };
  });

  return { table: dataWithId };
};

export const useDrillDownQuery = ({ filter, eventId, sessionId, sessionDatetime, flowId }: DrillDownProps) =>
  useQuery({
    queryKey: ["eventDrillDownReport", filter, eventId, sessionId, sessionDatetime, flowId],
    queryFn: getDrillDownQuery,
  });
